// Dashboard table for N-outcome sports (soccer Home/Away/Draw today). The
// binary GameTable assumes one away/home market pair per game; soccer is
// three independent binary markets (Away win / Home win / Draw), so each
// event renders through the SAME GameRows component the binary table uses —
// away Y/N row, home Y/N row, Draw row beneath — mirroring how GameDetail's
// Game Lines section lays out the NBA 1H 3-way winner. The Draw row is a
// `continuation` of the pair (same gameIndex), so all three outcomes share
// one row background — the zebra stripe
// composited over it — and the event reads as a single 3-way market.
//
// Clicks hand the chosen (event, outcome, venue) back to ValueDashboard,
// which synthesizes the per-outcome GameRow (`${event.key}|multi|${code}`)
// for the side panel + trade ticket — the table itself never builds tickets.
import type {
  MarketSide,
  MultiOutcomeEvent,
  PriceMode,
  SeriesOutcome,
  TeamSide,
} from './types';
import type { UserPositionSlice, UserOrderSlice } from '@/hooks/useUserKalshiPOS';
import { Th } from '@/components/atoms/TableCells';
import { useDisplaySettings } from '@/stores/displaySettings';
import { OddsFilterChips } from './OddsFilterChips';
import { opHeaderTooltip, OP_HEADER_LABEL } from '@/lib/opOdds';
import { useMarketStore } from '@/stores/marketStore';
import { GameRows } from './GameTable';
import {
  LIQ_HEADER_TOOLTIP_KALSHI,
  LIQ_HEADER_TOOLTIP_POLY,
  KALSHI_TOTAL_VOLUME_LABEL,
  POLY_TOTAL_VOLUME_LABEL,
  VOL_HEADER_TOOLTIP_KALSHI,
  VOL_HEADER_TOOLTIP_POLY,
} from './sharedBookCue';
import { multiOutcomeDrawRow, multiOutcomePairRow, multiOutcomeRoles } from './multiOutcome';

export interface OutcomeClickOpts {
  priceCents?: number;
  initialOrderSide?: 'yes' | 'no';
  postOnly?: boolean;
}

interface MultiOutcomeTableProps {
  events: MultiOutcomeEvent[];
  /** Table title ("Soccer Moneylines"). */
  titleLabel?: string;
  /** Kalshi series prefixes shown beside the title, as on GameTable. */
  seriesTicker?: string;
  marketSide: MarketSide;
  priceMode: PriceMode;
  /** Key of the outcome whose detail panel is open — the dashboard's
   *  expandedGameKey, shaped `${event.key}|multi|${outcome.code}` by
   *  synthesizeFromMultiOutcome. Null when no multi-outcome panel is up. */
  expandedOutcomeKey: string | null;
  /** Row click (no opts) opens/toggles the outcome's panel; pill + ladder
   *  clicks pass price/side opts through to seed the trade ticket. */
  onOutcomeClick: (
    event: MultiOutcomeEvent,
    outcome: SeriesOutcome,
    venue: 'kalshi' | 'polymarket',
    opts?: OutcomeClickOpts,
  ) => void;
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
  /** #902 — keep `events` in the order given instead of soonest-kickoff first,
   *  for the Today / This week discovery board, which passes them already
   *  ranked by volume. Each event's away / home / Draw rows stay together
   *  either way. Defaults to false: the soccer board sorts exactly as before. */
  preserveGroupOrder?: boolean;
}

export function MultiOutcomeTable({
  events,
  titleLabel,
  seriesTicker,
  marketSide,
  priceMode,
  expandedOutcomeKey,
  onOutcomeClick,
  positionByTicker,
  ordersByTicker,
  preserveGroupOrder = false,
}: MultiOutcomeTableProps) {
  'use memo';
  // Soonest kickoff first, unknown starts sinking — unless the caller supplies the order
  // (`preserveGroupOrder`), which is then kept as given. #201: the manual memo is
  // removed — this component is compiled (#197 wave A2) and the compiler caches the sort
  // itself. The table re-renders on every marketStore tick, so the array must stay
  // reference-stable across ticks that do not change `events`; that is measured, not
  // assumed — see docs/react-compiler-clusters/manual-memo-cleanup-201.md.
  const sorted = preserveGroupOrder ? events : [...events].sort((a, b) => {
    const cmp =
      (a.startTimeMs ?? Number.POSITIVE_INFINITY) - (b.startTimeMs ?? Number.POSITIVE_INFINITY);
    if (cmp !== 0) return cmp;
    return a.awayCode.localeCompare(b.awayCode);
  });

  // OP column lockstep with the shared GameRows (architecture §8 auto-hide):
  // multi-outcome/series/advance events don't carry OP coverage today, so
  // this stays hidden in practice — the lockstep exists so a future OP
  // expansion can't misalign this thead against the shared rows.
  const showOpOdds = useDisplaySettings((s) => s.showOpOdds);
  const hideOpColumn = useMarketStore(
    (s) => !showOpOdds || !s.hasOpOddsForAny(sorted.map((e) => e.key.split('|multi|')[0])),
  );

  // Same header semantics as GameTable: pre-game rows show kickoff time,
  // started rows show the score, mixed slates label both.
  const startedCount = sorted.filter((e) => e.gameState !== 'pre').length;
  const scoreColLabel =
    sorted.length === 0 || startedCount === sorted.length
      ? 'Score'
      : startedCount === 0
        ? 'Time'
        : 'Time / Score';

  return (
    // #826 — see GameTable: the section id namespaces this board's row
    // anchors so a game listed here and on another board stay distinct.
    <section data-view-anchor="multi-outcome" className="bg-card border-border rounded-lg border">
      <div className="border-border flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <h2 className="text-foreground text-lg font-semibold">{titleLabel ?? 'Moneylines'}</h2>
          {seriesTicker && (
            <>
              <span className="text-muted-foreground text-xs">&mdash;</span>
              <span className="text-muted-foreground font-mono text-xs">{seriesTicker}</span>
            </>
          )}
        </div>
        <OddsFilterChips />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse border-spacing-0">
          <thead className="bg-muted/50 border-border border-b">
            <tr>
              <Th className="w-28">Date</Th>
              <Th className="w-48">Team</Th>
              <Th
                className="w-28 text-center"
                title="Kickoff time + countdown until the game starts, then the live/final score."
              >
                {scoreColLabel}
              </Th>
              {/* Orders gutter (the retired UNBTD column's slot, #1192). */}
              <Th className="w-10" title="Your resting orders at each ladder price level">{''}</Th>
              {!hideOpColumn && (
                <Th
                  className="w-24 text-center"
                  title={opHeaderTooltip()}
                >
                  {OP_HEADER_LABEL}
                </Th>
              )}
              <Th className="w-32 text-center">Kalshi</Th>
              <Th className="w-24 text-right" title={VOL_HEADER_TOOLTIP_KALSHI}>{KALSHI_TOTAL_VOLUME_LABEL}</Th>
              <Th className="w-24 text-right" title={LIQ_HEADER_TOOLTIP_KALSHI}>K Liq</Th>
              <Th
                className="w-16 text-right"
                title={`Best-bid depth on the active ${marketSide.toUpperCase()} side. Green <50k / Yellow 50–300k / Red ≥300k.`}
              >
                K Q
              </Th>
              <Th
                className="w-16 text-right"
                title="Kalshi best-bid depth change over the last 1m on the active side."
              >
                K Δ/m
              </Th>
              <Th className="w-32 text-center">Poly</Th>
              <Th className="w-24 text-right" title={VOL_HEADER_TOOLTIP_POLY}>{POLY_TOTAL_VOLUME_LABEL}</Th>
              <Th className="w-24 text-right" title={LIQ_HEADER_TOOLTIP_POLY}>P Liq</Th>
              <Th
                className="w-16 text-right"
                title="Polymarket best-bid depth change over the last 1m on the active side."
              >
                P Δ/m
              </Th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((e, eventIndex) => {
              const { away, home, draw } = multiOutcomeRoles(e);
              const pair = away && home ? multiOutcomePairRow(e, away, home) : null;
              // The Draw row is a continuation of the team pair, never a row
              // of its own: a draw-only event (team legs rejected or not yet
              // grouped) must render nothing, not a standalone "Draw".
              const drawSynth = pair && draw ? multiOutcomeDrawRow(e, draw) : null;

              // Which row+side the open panel's outcome lives on. The panel
              // key is `${event.key}|multi|${code}` regardless of venue.
              const prefix = `${e.key}|multi|`;
              const expandedCode = expandedOutcomeKey?.startsWith(prefix)
                ? expandedOutcomeKey.slice(prefix.length)
                : null;
              const pairExpandedSide: TeamSide | null =
                expandedCode && away && expandedCode === away.code
                  ? 'away'
                  : expandedCode && home && expandedCode === home.code
                    ? 'home'
                    : null;
              const drawExpanded = expandedCode != null && draw != null && expandedCode === draw.code;

              const outcomeFor = (key: string, side: TeamSide): SeriesOutcome | undefined =>
                pair && key === pair.key ? (side === 'away' ? away : home) : draw;
              const handleToggle = (key: string, side: TeamSide) => {
                const o = outcomeFor(key, side);
                if (!o) return;
                onOutcomeClick(e, o, o.kalshi ? 'kalshi' : 'polymarket');
              };
              const handleTrade = (
                key: string,
                side: TeamSide,
                venue: 'kalshi' | 'polymarket',
                opts?: OutcomeClickOpts,
              ) => {
                const o = outcomeFor(key, side);
                if (!o) return;
                onOutcomeClick(e, o, venue, opts ?? {});
              };

              const shared = {
                marketSide,
                priceMode,
                onToggleExpand: handleToggle,
                onTradeClick: handleTrade,
                positionByTicker,
                ordersByTicker,
                showScores: true,
                // Knockout "To Advance" shares the 1X2's canonicalGameKey but is
                // a different market — its row must never show the 90-minute
                // result's OP fair prob ("—" instead).
                advanceConsensus: e.kind === 'advance',
                hideOpColumn,
                ringSlot: 'label' as const,
              };
              // groupSelected/groupExtendsBelow tie the pair + Draw into ONE
              // selection group: whichever outcome is expanded, the blue box
              // wraps all three rows (pair top rail → Draw bottom rail).
              return [
                pair && (
                  <GameRows
                    key={pair.key}
                    game={pair}
                    gameIndex={eventIndex}
                    expandedTeamSide={pairExpandedSide}
                    groupSelected={drawExpanded}
                    groupExtendsBelow={drawSynth != null}
                    {...shared}
                  />
                ),
                drawSynth && (
                  <GameRows
                    key={drawSynth.key}
                    game={drawSynth}
                    gameIndex={eventIndex}
                    continuation={pair != null}
                    singleSide="away"
                    oddsOutcome="draw"
                    expandedTeamSide={drawExpanded ? 'away' : null}
                    groupSelected={pairExpandedSide != null}
                    {...shared}
                  />
                ),
              ];
            })}
          </tbody>
        </table>
      </div>

      {events.length === 0 && (
        <div className="text-muted-foreground p-8 text-center">No games available</div>
      )}
    </section>
  );
}
